'use client';

import { Session } from '@pickleballinc/lib/session';
import { PaginationV2 } from '@pickleballinc/react-ui';
import { MyModulesNoResults } from '@pickleballinc/ui/components/global/myModules/MyModulesNoResults';
import { Url } from 'next/dist/shared/lib/router/router';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import { publicEnv } from '@/lib/env';
import { FavoriteMyLeagueCard } from '@/modules/league/my-leagues/components/FavoriteMyLeagueCard';
import { MyLeagueCard } from '@/modules/league/my-leagues/components/MyLeagueCard';
import AccessLimited from '@/modules/players/v2/tabs/components/AccessLimited';
import { ALL_MODULES_PAGE_SIZE } from '@/modules/players/v2/tabs/constants';
import { getNoResultsConfig } from '@/modules/players/v2/tabs/helpers';
import { useAuth } from '@/permissions/AuthProvider';
import { Permission } from '@/permissions/permission';
import { MyLeague } from '@/types/leagues.types';
import {
  PlayerData,
  PlayerParticipationType,
  playerParticipationTypeMap,
} from '@/types/players.types';
import { GetPBApproveToCreatePermissionRes } from '@/utils/actions/leagues/getPBApproveToCreatePermission';
import isValidURL from '@/utils/helpers/isValidURL';

interface LeagueWrapperProps {
  leagues: MyLeague[];
  player: PlayerData;
  user?: Session;
  createPerms: GetPBApproveToCreatePermissionRes;
  totalRecords: number;
}

export const LeagueWrapper = ({
  leagues,
  player,
  user,
  createPerms,
  totalRecords,
}: LeagueWrapperProps) => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { hasPermission } = useAuth();
  const isPickleballSuperAdmin = hasPermission(Permission.SUPER_ADMIN);
  const isPBSuperAdmin = hasPermission(Permission.PICKLEBALL_BRACKETS_ADMIN);
  const isSuperAdmin = isPBSuperAdmin || isPickleballSuperAdmin;
  const isUserAlsoPlayer = user?.pbUuid?.toLowerCase() === player?.pbUuid?.toLowerCase();

  const leagueType = searchParams.get('league_type') || 'registeredfor';
  const statusLeague = searchParams.get('status') || 'current';
  const page = Number(searchParams?.get('page') || 1);
  const currentLeagueType = playerParticipationTypeMap[leagueType];

  const isManage = currentLeagueType === PlayerParticipationType.Managing;
  const isFavorites = currentLeagueType === PlayerParticipationType.Favorited;
  const isRegistered = currentLeagueType === PlayerParticipationType.RegisteredFor;

  const canManageLeague = isManage && (isUserAlsoPlayer || isSuperAdmin);
  const canViewFavorites = isUserAlsoPlayer || isSuperAdmin;
  const hasLeagues = leagues?.length > 0;

  const shouldRenderFavoriteCard =
    isRegistered || canManageLeague || (canViewFavorites && isUserAlsoPlayer);

  const [createALeagueURL, setCreateALeagueURL] = useState<Url>('');

  useEffect(() => {
    if (user?.uuid && createPerms?.ApprovedToCreate && createPerms.URLPath) {
      if (isValidURL(createPerms.URLPath)) {
        const url = new URL(createPerms.URLPath);
        url.searchParams.set('rurl', window.location.href);
        setCreateALeagueURL(url);
      } else {
        const url = new URL(`${publicEnv.NEXT_PUBLIC_PBRACKETS_URL}/${createPerms.URLPath}`);
        url.searchParams.set('rurl', window.location.href);
        setCreateALeagueURL(url);
      }
    } else {
      const url = new URL(publicEnv.NEXT_PUBLIC_CONTACT_FORM_URL);
      url.pathname += 'contacts/league/create';
      setCreateALeagueURL(url);
    }
  }, [createPerms, user]);

  const handlePageChange = (newPage: number) => {
    router.replace(`?status=${statusLeague}&league_type=${leagueType}&page=${newPage}`);
  };

  const noResultsConfig = getNoResultsConfig({
    moduleType: 'league',
    isManage,
    isRegistered,
    player,
    isUserAlsoPlayer,
    createUrl: createALeagueURL.toString(),
  });

  return (
    <div className='flex flex-col items-center gap-6 sm:gap-10 justify-center'>
      {hasLeagues && (isRegistered || canManageLeague || canViewFavorites) ? (
        <>
          <div className='mx-auto !max-w-7xl grid grid-cols-1 min-[500px]:grid-cols-2 gap-3 py-4 md:grid-cols-3 md:gap-6 lg:grid-cols-4'>
            {leagues.map((league) => {
              if (shouldRenderFavoriteCard) {
                return (
                  <FavoriteMyLeagueCard
                    key={`favorite-league-${league.LeagueID}`}
                    league={league}
                    isManageLeague={isManage}
                    isManagingCard={isManage}
                    isPlayerProfileLeaguesPage
                  />
                );
              } else {
                return (
                  <MyLeagueCard
                    key={`league-${league.LeagueID}`}
                    league={league}
                    isManageLeague={isManage}
                    isManagingCard={isManage}
                    shouldRenderFavoriteCard={shouldRenderFavoriteCard}
                  />
                );
              }
            })}
          </div>
          {totalRecords > ALL_MODULES_PAGE_SIZE && (
            <PaginationV2
              totalNumberOfPages={Math.ceil(totalRecords / ALL_MODULES_PAGE_SIZE)}
              currentPage={page}
              onPreviousClick={() => handlePageChange(page - 1)}
              onNextClick={() => handlePageChange(page + 1)}
              onPageClick={(newPage) => handlePageChange(newPage)}
            />
          )}
        </>
      ) : (!canManageLeague && isManage) || (!canViewFavorites && isFavorites) ? (
        <AccessLimited />
      ) : (
        <div className='mx-auto max-w-[442px]'>
          <MyModulesNoResults {...noResultsConfig} />
        </div>
      )}
    </div>
  );
};
